/**
 * "2 days ago", for a timestamp someone is scanning a table for.
 *
 * Built on the browser's own `Intl.RelativeTimeFormat`, so no dependency.
 * `numeric: "auto"` lets it say "yesterday" rather than "1 day ago". Past a
 * week a relative phrase stops helping ("13 days ago" is harder to place than
 * a date), so it falls back to day and short month.
 *
 * `now` is a parameter so the function is pure and the tests are not
 * wall-clock dependent.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) {
    return "";
  }

  const elapsed = now.getTime() - then.getTime();
  if (elapsed < MINUTE) {
    return "just now";
  }

  const format = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (elapsed < HOUR) {
    return format.format(-Math.floor(elapsed / MINUTE), "minute");
  }
  if (elapsed < DAY) {
    return format.format(-Math.floor(elapsed / HOUR), "hour");
  }
  if (elapsed < WEEK) {
    return format.format(-Math.floor(elapsed / DAY), "day");
  }

  return then.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
